/**
 * Business Insights Engine
 * Generates AI-powered insights and recommendations based on business metrics
 */

import React from 'react'
import i18n from '../lib/i18n'
import { calculateHealthScore } from './businessHealthScore'
import { generateComprehensiveTrendAnalysis } from './trendAnalysis'
import { getMetricStatus } from './performanceThresholds'

/**
 * Generate comprehensive business insights
 */
export const generateBusinessInsights = (currentMetrics, historicalData, industryBenchmarks = null) => {
  if (!currentMetrics) {
    return {
      insights: [],
      recommendations: [],
      alerts: [],
      opportunities: []
    }
  }

  const healthData = calculateHealthScore(currentMetrics)
  const trendAnalysis = historicalData && historicalData.length >= 3 
    ? generateComprehensiveTrendAnalysis(historicalData)
    : null

  const insights = []
  const recommendations = []
  const alerts = []
  const opportunities = []

  // Health Score Insights
  const healthInsights = generateHealthScoreInsights(healthData)
  insights.push(...healthInsights.insights)
  recommendations.push(...healthInsights.recommendations)
  alerts.push(...healthInsights.alerts)

  // Trend-based Insights
  if (trendAnalysis) {
    const trendInsights = generateTrendBasedInsights(trendAnalysis)
    insights.push(...trendInsights.insights)
    recommendations.push(...trendInsights.recommendations)
    alerts.push(...trendInsights.alerts)
    opportunities.push(...trendInsights.opportunities)
  }

  // Performance Pattern Insights
  const patternInsights = generatePerformancePatternInsights(currentMetrics, historicalData)
  insights.push(...patternInsights.insights)
  recommendations.push(...patternInsights.recommendations)
  opportunities.push(...patternInsights.opportunities)

  // Industry Benchmarking (if available)
  if (industryBenchmarks) {
    const benchmarkInsights = generateBenchmarkInsights(currentMetrics, industryBenchmarks)
    insights.push(...benchmarkInsights.insights)
    opportunities.push(...benchmarkInsights.opportunities)
  }

  return {
    insights: insights.slice(0, 8), // Limit to top insights
    recommendations: recommendations.slice(0, 6),
    alerts: alerts.slice(0, 4),
    opportunities: opportunities.slice(0, 5)
  }
}

/**
 * Generate insights based on health score
 */
const generateHealthScoreInsights = (healthData) => {
  const { score, status, breakdown } = healthData
  const insights = []
  const recommendations = []
  const alerts = []

  // Overall health insights
  if (score >= 85) {
    insights.push({
      type: 'success',
      title: i18n.t('insights.generated.exceptionalPerformance.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.exceptionalPerformance.message', { ns: 'dashboard', score }),
      priority: 'high',
      category: 'performance'
    })
  } else if (score < 50) {
    alerts.push({
      type: 'critical',
      title: i18n.t('insights.generated.businessHealthCritical.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.businessHealthCritical.message', { ns: 'dashboard', score }),
      priority: 'critical',
      category: 'health',
      actionRequired: true
    })
  }

  // Metric-specific insights
  Object.entries(breakdown).forEach(([metric, data]) => {
    if (data.status === 'poor' && data.weight >= 20) {
      alerts.push({
        type: 'warning',
        title: i18n.t('insights.generated.metricUnderperforming.title', { ns: 'dashboard', metric: formatMetricName(metric) }),
        message: i18n.t('insights.generated.metricUnderperforming.message', { ns: 'dashboard', weight: data.weight }),
        priority: 'high',
        category: 'metrics',
        metric,
        actionRequired: true
      })
    } else if (data.status === 'excellent' && data.weight >= 20) {
      insights.push({
        type: 'positive',
        title: i18n.t('insights.generated.strongMetric.title', { ns: 'dashboard', metric: formatMetricName(metric) }),
        message: i18n.t('insights.generated.strongMetric.message', { ns: 'dashboard', metric: formatMetricName(metric).toLowerCase() }),
        priority: 'medium',
        category: 'performance',
        metric
      })
    }
  })

  // Generate targeted recommendations
  const poorMetrics = Object.entries(breakdown)
    .filter(([_, data]) => data.status === 'poor' || data.status === 'warning')
    .sort((a, b) => b[1].weight - a[1].weight) // Sort by weight (importance)

  poorMetrics.slice(0, 3).forEach(([metric, data]) => {
    const rec = generateMetricRecommendation(metric, data, 'health_score')
    if (rec) recommendations.push(rec)
  })

  return { insights, recommendations, alerts }
}

/**
 * Generate insights based on trend analysis
 */
const generateTrendBasedInsights = (trendAnalysis) => {
  const { revenue, profitMargin, seasonality, momentum } = trendAnalysis
  const insights = []
  const recommendations = []
  const alerts = []
  const opportunities = []

  // Momentum insights
  if (momentum === 'accelerating') {
    insights.push({
      type: 'success',
      title: i18n.t('insights.generated.strongBusinessMomentum.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.strongBusinessMomentum.message', { ns: 'dashboard' }),
      priority: 'high',
      category: 'growth'
    })
  } else if (momentum === 'decelerating') {
    alerts.push({
      type: 'critical',
      title: i18n.t('insights.generated.decliningBusinessMomentum.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.decliningBusinessMomentum.message', { ns: 'dashboard' }),
      priority: 'critical',
      category: 'decline',
      actionRequired: true
    })
  }

  // Revenue trend insights
  if (revenue.direction === 'growing' && revenue.strength === 'strong') {
    insights.push({
      type: 'positive',
      title: i18n.t('insights.generated.consistentRevenueGrowth.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.consistentRevenueGrowth.message', { ns: 'dashboard', amount: Math.abs(revenue.slope).toLocaleString(), confidence: revenue.r2 }),
      priority: 'high',
      category: 'revenue'
    })
    
    opportunities.push({
      type: 'growth',
      title: i18n.t('insights.generated.scaleCurrentStrategy.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.scaleCurrentStrategy.message', { ns: 'dashboard' }),
      priority: 'high',
      category: 'scaling'
    })
  } else if (revenue.direction === 'declining' && revenue.strength === 'strong') {
    alerts.push({
      type: 'warning',
      title: i18n.t('insights.generated.consistentRevenueDecline.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.consistentRevenueDecline.message', { ns: 'dashboard', amount: Math.abs(revenue.slope).toLocaleString() }),
      priority: 'critical',
      category: 'revenue',
      actionRequired: true
    })
  }

  // Profit margin insights
  if (profitMargin.direction === 'declining' && profitMargin.strength === 'strong') {
    alerts.push({
      type: 'warning',
      title: i18n.t('insights.generated.erodingProfitMargins.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.erodingProfitMargins.message', { ns: 'dashboard' }),
      priority: 'high',
      category: 'profitability',
      actionRequired: true
    })

    recommendations.push({
      type: 'strategic',
      title: i18n.t('insights.generated.marginRecoveryStrategy.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.marginRecoveryStrategy.message', { ns: 'dashboard' }),
      priority: 'high',
      category: 'profitability',
      actions: [
        i18n.t('insights.generated.actions.reviewPricingStrategy', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.analyzeCostStructure', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.optimizeProductMix', { ns: 'dashboard' })
      ]
    })
  }

  // Seasonality insights
  if (seasonality.hasSeasonality && seasonality.strength > 40) {
    insights.push({
      type: 'insight',
      title: i18n.t('insights.generated.strongSeasonalPattern.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.strongSeasonalPattern.message', { ns: 'dashboard', peakMonth: seasonality.peakMonth, lowMonth: seasonality.lowMonth }),
      priority: 'medium',
      category: 'seasonality'
    })

    opportunities.push({
      type: 'planning',
      title: i18n.t('insights.generated.seasonalOptimization.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.seasonalOptimization.message', { ns: 'dashboard' }),
      priority: 'medium',
      category: 'operations'
    })
  }

  return { insights, recommendations, alerts, opportunities }
}

/**
 * Generate performance pattern insights
 */
const generatePerformancePatternInsights = (currentMetrics, historicalData) => {
  const insights = []
  const recommendations = []
  const opportunities = []

  if (!historicalData || historicalData.length < 2) {
    return { insights, recommendations, opportunities }
  }

  // Analyze metric correlations
  const correlationInsights = analyzeMetricCorrelations(historicalData)
  insights.push(...correlationInsights.insights)
  opportunities.push(...correlationInsights.opportunities)

  // Analyze consistency
  const consistencyInsights = analyzeMetricConsistency(historicalData)
  insights.push(...consistencyInsights.insights)
  recommendations.push(...consistencyInsights.recommendations)

  // Detect breakthrough months
  const breakthroughInsights = detectBreakthroughPerformance(historicalData)
  insights.push(...breakthroughInsights.insights)
  opportunities.push(...breakthroughInsights.opportunities)

  return { insights, recommendations, opportunities }
}

/**
 * Analyze correlations between metrics
 */
const analyzeMetricCorrelations = (historicalData) => {
  const insights = []
  const opportunities = []

  // Simple correlation analysis between leads and revenue
  const leadRevenueCorr = calculateSimpleCorrelation(
    historicalData.map(d => d.leads || 0),
    historicalData.map(d => d.revenue || 0)
  )

  if (leadRevenueCorr > 0.7) {
    insights.push({
      type: 'insight',
      title: i18n.t('insights.generated.strongLeadRevenueCorrelation.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.strongLeadRevenueCorrelation.message', { ns: 'dashboard' }),
      priority: 'medium',
      category: 'correlation'
    })

    opportunities.push({
      type: 'marketing',
      title: i18n.t('insights.generated.leadGenerationInvestment.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.leadGenerationInvestment.message', { ns: 'dashboard' }),
      priority: 'high',
      category: 'growth'
    })
  }

  return { insights, opportunities }
}

/**
 * Analyze metric consistency
 */
const analyzeMetricConsistency = (historicalData) => {
  const insights = []
  const recommendations = []

  // Calculate coefficient of variation for key metrics
  const revenueCV = calculateCoeffientOfVariation(historicalData.map(d => d.revenue || 0))
  const profitCV = calculateCoeffientOfVariation(historicalData.map(d => d.netProfit || 0))

  if (revenueCV > 0.3) {
    insights.push({
      type: 'warning',
      title: i18n.t('insights.generated.highRevenueVariability.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.highRevenueVariability.message', { ns: 'dashboard' }),
      priority: 'medium',
      category: 'stability'
    })

    recommendations.push({
      type: 'operational',
      title: i18n.t('insights.generated.stabilizeRevenueStreams.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.stabilizeRevenueStreams.message', { ns: 'dashboard' }),
      priority: 'medium',
      category: 'stability',
      actions: [
        i18n.t('insights.generated.actions.exploreRecurringRevenue', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.diversifyCustomerBase', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.improveForecasting', { ns: 'dashboard' })
      ]
    })
  }

  return { insights, recommendations }
}

/**
 * Detect breakthrough performance months
 */
const detectBreakthroughPerformance = (historicalData) => {
  const insights = []
  const opportunities = []

  if (historicalData.length < 3) return { insights, opportunities }

  // Find months with exceptional performance
  const revenues = historicalData.map(d => d.revenue || 0)
  const avgRevenue = revenues.reduce((sum, val) => sum + val, 0) / revenues.length
  const stdDev = Math.sqrt(revenues.reduce((sum, val) => sum + Math.pow(val - avgRevenue, 2), 0) / revenues.length)

  const breakthroughThreshold = avgRevenue + (1.5 * stdDev)
  const breakthroughMonths = historicalData.filter(d => (d.revenue || 0) > breakthroughThreshold)

  if (breakthroughMonths.length > 0) {
    insights.push({
      type: 'success',
      title: i18n.t('insights.generated.breakthroughPerformanceDetected.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.breakthroughPerformanceDetected.message', { ns: 'dashboard', count: breakthroughMonths.length }),
      priority: 'high',
      category: 'achievement'
    })

    opportunities.push({
      type: 'replication',
      title: i18n.t('insights.generated.replicateSuccessFactors.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.replicateSuccessFactors.message', { ns: 'dashboard' }),
      priority: 'high',
      category: 'optimization'
    })
  }

  return { insights, opportunities }
}

/**
 * Generate benchmark insights (if industry data available)
 */
const generateBenchmarkInsights = (currentMetrics, benchmarks) => {
  const insights = []
  const opportunities = []

  // Compare key metrics to industry benchmarks
  if (benchmarks.profitMargin && currentMetrics.profitMargin) {
    const diff = currentMetrics.profitMargin - benchmarks.profitMargin
    if (diff > 5) {
      insights.push({
        type: 'competitive',
        title: i18n.t('insights.generated.aboveIndustryProfitMargin.title', { ns: 'dashboard' }),
        message: i18n.t('insights.generated.aboveIndustryProfitMargin.message', { ns: 'dashboard', diff: diff.toFixed(1) }),
        priority: 'high',
        category: 'benchmarking'
      })
    } else if (diff < -5) {
      opportunities.push({
        type: 'improvement',
        title: i18n.t('insights.generated.marginImprovementOpportunity.title', { ns: 'dashboard' }),
        message: i18n.t('insights.generated.marginImprovementOpportunity.message', { ns: 'dashboard', diff: Math.abs(diff).toFixed(1) }),
        priority: 'high',
        category: 'benchmarking'
      })
    }
  }

  return { insights, opportunities }
}

/**
 * Generate metric-specific recommendations
 */
const generateMetricRecommendation = (metric, data, context) => {
  const recommendations = {
    profitMargin: {
      type: 'financial',
      title: i18n.t('insights.generated.metricRecommendations.profitMargin.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.metricRecommendations.profitMargin.message', { ns: 'dashboard' }),
      priority: 'high',
      category: 'profitability',
      actions: [
        i18n.t('insights.generated.actions.reviewPricingStrategy', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.optimizeCostStructure', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.improveOperationalEfficiency', { ns: 'dashboard' })
      ]
    },
    conversionRate: {
      type: 'sales',
      title: i18n.t('insights.generated.metricRecommendations.conversionRate.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.metricRecommendations.conversionRate.message', { ns: 'dashboard' }),
      priority: 'high',
      category: 'sales',
      actions: [
        i18n.t('insights.generated.actions.refineLeadQualification', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.improveSalesTraining', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.optimizeSalesFunnel', { ns: 'dashboard' })
      ]
    },
    revenueGrowth: {
      type: 'growth',
      title: i18n.t('insights.generated.metricRecommendations.revenueGrowth.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.metricRecommendations.revenueGrowth.message', { ns: 'dashboard' }),
      priority: 'medium',
      category: 'growth',
      actions: [
        i18n.t('insights.generated.actions.increaseMarketingSpend', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.expandMarketReach', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.developNewRevenueStreams', { ns: 'dashboard' })
      ]
    },
    expenseRatio: {
      type: 'operational',
      title: i18n.t('insights.generated.metricRecommendations.expenseRatio.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.metricRecommendations.expenseRatio.message', { ns: 'dashboard' }),
      priority: 'high',
      category: 'efficiency',
      actions: [
        i18n.t('insights.generated.actions.auditExpenseCategories', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.negotiateVendorContracts', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.improveOperationalEfficiency', { ns: 'dashboard' })
      ]
    },
    netProfit: {
      type: 'financial',
      iconType: 'growth',
      title: i18n.t('insights.generated.metricRecommendations.netProfit.title', { ns: 'dashboard' }),
      message: i18n.t('insights.generated.metricRecommendations.netProfit.message', { ns: 'dashboard' }),
      priority: 'high',
      category: 'profitability',
      actions: [
        i18n.t('insights.generated.actions.balanceGrowthAndProfitability', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.optimizePricing', { ns: 'dashboard' }),
        i18n.t('insights.generated.actions.controlCosts', { ns: 'dashboard' })
      ]
    }
  }

  return recommendations[metric] || null
}

/**
 * Helper functions
 */
const formatMetricName = (metric) => {
  const names = {
    profitMargin: i18n.t('kpi.profitMargin.title', { ns: 'dashboard' }),
    conversionRate: i18n.t('kpi.conversionRate.title', { ns: 'dashboard' }),
    revenueGrowth: i18n.t('kpi.revenueGrowth.title', { ns: 'dashboard' }),
    expenseRatio: i18n.t('kpi.expenseRatio.title', { ns: 'dashboard' }),
    netProfit: i18n.t('kpi.netProfit.title', { ns: 'dashboard' }),
    revenue: i18n.t('kpi.revenue.title', { ns: 'dashboard' })
  }

  return names[metric] || metric.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
}

const calculateSimpleCorrelation = (x, y) => {
  if (x.length !== y.length || x.length === 0) return 0
  
  const n = x.length
  const sumX = x.reduce((sum, val) => sum + val, 0)
  const sumY = y.reduce((sum, val) => sum + val, 0)
  const sumXY = x.reduce((sum, val, i) => sum + val * y[i], 0)
  const sumXX = x.reduce((sum, val) => sum + val * val, 0)
  const sumYY = y.reduce((sum, val) => sum + val * val, 0)
  
  const numerator = n * sumXY - sumX * sumY
  const denominator = Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY))
  
  return denominator === 0 ? 0 : numerator / denominator
}

const calculateCoeffientOfVariation = (values) => {
  if (values.length === 0) return 0
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length
  if (mean === 0) return 0
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length
  return Math.sqrt(variance) / mean
}

/**
 * Get insight icon based on type
 * Uses simple SVG icons instead of emojis for a more professional look.
 */

const InsightIconPositive = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path
      d="M7 17L17 7M8 7h9v9"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const InsightIconWarning = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path
      d="M12 3.5l8.5 15H3.5L12 3.5z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="M12 10v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="12" cy="16.5" r="0.9" fill="currentColor" />
  </svg>
)

const InsightIconCritical = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <rect x="4" y="4" width="16" height="16" rx="3" fill="currentColor" opacity="0.15" />
    <path d="M8 8l8 8M16 8l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
)

const InsightIconLightbulb = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path
      d="M9 18h6M10 20h4"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path
      d="M12 4a6 6 0 00-3.5 10.9c.4.3.7.8.9 1.3l.2.8h4.8l.2-.8c.2-.5.5-1 .9-1.3A6 6 0 0012 4z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
  </svg>
)

const InsightIconTrophy = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path
      d="M9 4h6v3a4 4 0 01-8 0V4z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path
      d="M7 4H5a2 2 0 002 3M17 4h2a2 2 0 01-2 3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
    <path d="M10 14h4v1.5a2 2 0 01-2 2H9" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
)

const InsightIconGrowth = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path d="M4 19h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M7 15l3-4 3 3 4-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M14.5 7H18v3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
)

const InsightIconTarget = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.4" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" />
  </svg>
)

const InsightIconDefault = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <rect x="5" y="5" width="4" height="12" rx="1" fill="currentColor" opacity="0.7" />
    <rect x="10" y="9" width="4" height="8" rx="1" fill="currentColor" opacity="0.5" />
    <rect x="15" y="7" width="4" height="10" rx="1" fill="currentColor" opacity="0.9" />
  </svg>
)

export const getInsightIcon = (type) => {
  const icons = {
    success: <InsightIconPositive />,
    positive: <InsightIconPositive />,
    warning: <InsightIconWarning />,
    critical: <InsightIconCritical />,
    insight: <InsightIconLightbulb />,
    competitive: <InsightIconTrophy />,
    growth: <InsightIconGrowth />,
    achievement: <InsightIconTarget />
  }
  return icons[type] || <InsightIconDefault />
}

/**
 * Get priority color classes
 */
export const getPriorityColors = (priority) => {
  const colors = {
    critical: 'text-red-700 bg-red-50 border-red-200',
    high: 'text-orange-700 bg-orange-50 border-orange-200',
    medium: 'text-yellow-800 bg-yellow-50 border-yellow-200',
    low: 'text-green-700 bg-green-50 border-green-200'
  }
  return colors[priority] || 'text-gray-700 bg-gray-50 border-gray-200'
}
